/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editor actions that add, duplicate or delete whole layers. Each is one undo step, and each
 * leaves the selection pointing at something that exists.
 */
import {
  addImportedLayer,
  duplicateElements,
  flattenLayerTransforms,
  groupElements,
  hasAnimatedTransformAncestor,
  moveElements,
  removeElements,
  splitPathLayer,
  ungroupElements,
  type MoveTarget,
} from '../../core/import/layers';
import type { ElementId, Project } from '../../core/model/types';
import { clearSelection, selectElements, setExpanded } from './actions';
import { toast } from '../ui/toast';
import type { Store } from './store';

/** Adds an imported SVG as a new group, selects it and returns its ID. */
export function addSvgLayer(store: Store, imported: Project): ElementId {
  let groupId = '';
  store.updateProject('Add SVG layer', (draft) => {
    groupId = addImportedLayer(draft, imported);
  });
  setExpanded(store, [groupId], true);
  selectElements(store, [groupId]);
  return groupId;
}

/** Deletes the given elements and everything inside them. Returns how many were removed. */
export function deleteLayers(store: Store, ids: readonly ElementId[]): number {
  let removed: ElementId[] = [];
  store.updateProject('Delete layer', (draft) => {
    removed = removeElements(draft, ids);
  });
  if (removed.length > 0) clearSelection(store);
  return removed.length;
}

/** Copies the given elements, with their animation, beside the originals and selects the copies. */
export function duplicateLayers(store: Store, ids: readonly ElementId[]): ElementId[] {
  let created: ElementId[] = [];
  store.updateProject('Duplicate layer', (draft) => {
    created = duplicateElements(draft, ids);
  });
  if (created.length > 0) selectElements(store, created);
  return created;
}

/** The selected elements that are layers in the tree. */
function selectedLayerIds(store: Store): ElementId[] {
  const { project, editor } = store.getState();
  // Gradient stops can be selected, but they belong to a gradient, not to the layer tree.
  return editor.selection.elementIds.filter((id) => project.elements[id]?.tag !== 'stop');
}

/** Duplicates the selected elements. Returns the IDs of the copies. */
export function duplicateSelectedLayers(store: Store): ElementId[] {
  return duplicateLayers(store, selectedLayerIds(store));
}

/** Deletes the selected elements. Returns how many were removed. */
export function deleteSelectedLayers(store: Store): number {
  return deleteLayers(store, selectedLayerIds(store));
}

/** What an arranging action did: whether anything changed, and anything the user should know. */
export interface ArrangeOutcome {
  changed: boolean;
  /** Why nothing changed, or why only part of the request was carried out. */
  reason?: string;
  /** A side effect worth mentioning even though the action worked. */
  warning?: string;
}

const LOST_MOTION_WARNING =
  "Moved out of an animated group, so it no longer follows that group's motion.";

/** Tells the user what an arranging action did not do, or what it cost them. */
export function reportArrange(outcome: ArrangeOutcome): void {
  if (outcome.reason) toast.info(outcome.reason);
  if (outcome.warning) toast.warning(outcome.warning);
}

/** The ids that can be arranged: layers only, in the order given. */
function arrangeable(store: Store, ids: readonly ElementId[]): ElementId[] {
  const { project } = store.getState();
  return ids.filter((id) => project.elements[id] && project.elements[id].tag !== 'stop');
}

/** Moves layers to a new parent and position as one undo step, keeping them where they are on screen. */
export function moveLayers(
  store: Store,
  ids: readonly ElementId[],
  target: MoveTarget,
): ArrangeOutcome {
  const layers = arrangeable(store, ids);
  const { project } = store.getState();
  const losesMotion = layers.some(
    (id) =>
      project.elements[id]?.parentId !== target.parentId &&
      hasAnimatedTransformAncestor(project, id),
  );
  let moved: ElementId[] = [];
  let reason: string | undefined;
  store.updateProject('Move layer', (draft) => {
    const result = moveElements(draft, layers, target);
    moved = result.moved;
    reason = result.reason;
  });
  if (moved.length === 0) return { changed: false, ...(reason ? { reason } : {}) };
  if (target.parentId) setExpanded(store, [target.parentId], true);
  selectElements(store, moved);
  return { changed: true, ...(losesMotion ? { warning: LOST_MOTION_WARNING } : {}) };
}

/** Shared parent of the layers, or undefined when they sit in different places. */
function sharedParent(store: Store, ids: readonly ElementId[]): ElementId | null | undefined {
  const { project } = store.getState();
  const parents = new Set(ids.map((id) => project.elements[id]?.parentId ?? null));
  return parents.size === 1 ? ([...parents][0] ?? null) : undefined;
}

/**
 * Moves the selected layers one place up the list (further forward, drawn later) or down it
 * (further back) among their siblings.
 */
export function nudgeLayers(
  store: Store,
  ids: readonly ElementId[],
  direction: 'up' | 'down',
): ArrangeOutcome {
  const layers = arrangeable(store, ids);
  const parentId = sharedParent(store, layers);
  if (layers.length === 0) return { changed: false };
  if (parentId === undefined) {
    return { changed: false, reason: 'Select layers that are in the same group to reorder them.' };
  }
  const { project } = store.getState();
  const siblings = parentId ? (project.elements[parentId]?.childIds ?? []) : project.rootIds;
  const positions = layers.map((id) => siblings.indexOf(id)).sort((a, b) => a - b);
  const first = positions[0] ?? 0;
  const last = positions[positions.length - 1] ?? 0;
  if (direction === 'down' && first === 0) return { changed: false };
  if (direction === 'up' && last === siblings.length - 1) return { changed: false };
  const index = direction === 'down' ? first - 1 : last - layers.length + 2;
  return moveLayers(store, layers, { parentId, index });
}

/** Moves the selected layers out of their group, to just after it. */
export function moveOutOfGroup(store: Store, ids: readonly ElementId[]): ArrangeOutcome {
  const layers = arrangeable(store, ids);
  const parentId = sharedParent(store, layers);
  if (layers.length === 0) return { changed: false };
  if (parentId === undefined) {
    return { changed: false, reason: 'Select layers that are in the same group.' };
  }
  if (parentId === null)
    return { changed: false, reason: 'This layer is already at the top level.' };
  const { project } = store.getState();
  const grandparent = project.elements[parentId]?.parentId ?? null;
  const siblings = grandparent ? (project.elements[grandparent]?.childIds ?? []) : project.rootIds;
  return moveLayers(store, layers, {
    parentId: grandparent,
    index: siblings.indexOf(parentId) + 1,
  });
}

/** Wraps the selected layers in a new group, selects it and shows its contents. */
export function groupSelectedLayers(store: Store): ArrangeOutcome {
  const layers = arrangeable(store, selectedLayerIds(store));
  if (layers.length === 0) return { changed: false, reason: 'Select layers to group.' };
  let groupId: ElementId = '';
  store.updateProject('Group layers', (draft) => {
    groupId = groupElements(draft, layers) ?? '';
  });
  if (groupId === '') return { changed: false };
  setExpanded(store, [groupId], true);
  selectElements(store, [groupId]);
  return { changed: true };
}

/** Dissolves the selected groups and selects the layers that were inside them. */
export function ungroupSelectedLayers(store: Store): ArrangeOutcome {
  const layers = arrangeable(store, selectedLayerIds(store));
  let freed: ElementId[] = [];
  let reason: string | undefined;
  store.updateProject('Ungroup layers', (draft) => {
    const result = ungroupElements(draft, layers);
    freed = result.freed;
    reason = result.reason;
  });
  if (freed.length > 0) selectElements(store, freed);
  return { changed: freed.length > 0, ...(reason ? { reason } : {}) };
}

/** Splits each selected compound path into one layer per shape, and selects the new groups. */
export function splitSelectedPaths(store: Store): ArrangeOutcome {
  const layers = arrangeable(store, selectedLayerIds(store));
  const groups: ElementId[] = [];
  let reason: string | undefined;
  store.updateProject('Split into parts', (draft) => {
    for (const id of layers) {
      const result = splitPathLayer(draft, id);
      if (result.parts.length > 0) groups.push(id);
      else reason ??= result.reason;
    }
  });
  if (groups.length === 0) return { changed: false, ...(reason ? { reason } : {}) };
  setExpanded(store, groups, true);
  selectElements(store, groups);
  return { changed: true, ...(reason ? { reason } : {}) };
}

/** Folds the selected paths' transforms into their geometry. */
export function flattenSelectedTransforms(store: Store): ArrangeOutcome {
  const layers = arrangeable(store, selectedLayerIds(store));
  let done: ElementId[] = [];
  let reason: string | undefined;
  store.updateProject('Flatten transform', (draft) => {
    const result = flattenLayerTransforms(draft, layers);
    done = result.done;
    reason = result.reason;
  });
  return { changed: done.length > 0, ...(reason ? { reason } : {}) };
}
